/**
 * PracticePage — who practised, what they did, what came of it, and how it was rated.
 *
 * A "practice drill" is a rehearsal: the coach plays a client who is hiding a real need and
 * an objection he will not say out loud, a relationship manager tries to crack him, and the
 * coach then drops the character and debriefs him. Spec
 * `docs/specs/2026-08-29-roleplay-practice-mode.md` §19 makes this one factor in the weekly
 * review a new hire goes through, so this page is read by a manager about a named person.
 *
 * TWO BLOCKS AND NOTHING ELSE: one row per person for the period, then one row per session
 * newest first, with the judge's assessment and the debrief behind a click. Clicking a
 * person scopes the session list to him through the global user filter both queries already
 * read — no second selection state to disagree with the chip in the filter bar.
 *
 * THE SCORE IS SHOWN, and that is the owner's ruling of 2026-09-02 overturning §19.4's "no
 * single number" (`docs/practice-briefs/2026-09-02-dashboard-score/PLAN.md`): the number is
 * management-only, the RM never sees it and is never told the mechanics, so there is no
 * formula for him to farm. What survives is a rule on this page — a score never appears
 * without the coverage beside it (how many distinct scenes, at what difficulty), and a mean
 * taken over fewer drills than he ran says so on the row.
 *
 * ── THREE THINGS THAT STILL CONSTRAIN EVERY LINE BELOW ──────────────────────────────
 *
 * 1. IT SAYS HOW FRESH IT IS, ABOVE THE NUMBERS. These counts come from a MIRROR of the
 *    trace store filled by a sync worker. When that worker stalls the mirror silently stops
 *    growing and every query still succeeds — so an empty page is indistinguishable from a
 *    quiet week, and that misreading ends up in somebody's review. The freshness line is not
 *    chrome; it is the sentence that stops a zero being read as a fact.
 * 2. NO SCENE TEXT. A scene's persona, hidden need, hidden objection and unlock condition
 *    are the ANSWERS to a drill; a manager who reads one has spent a drill his RM has not
 *    run yet. Only the scene id, its difficulty and its turn ceiling ever reach this page.
 *    The debrief is different — it is what the coach wrote about a conversation that has
 *    already finished — and so is the judgement, which quotes the RM's own words back.
 * 3. NOTHING HERE REACHES THE SALESPERSON (§19.1). No score, no judgement, no rating enters
 *    his debrief, his history or any prompt. The route is admin-gated.
 *
 * A drill in flight is also not an abandoned one: it carries no close reason until it ends,
 * so "started minus finished" pools the drills he walked away from with the one he is in
 * right now. That difference is never printed; the session list names each outcome instead.
 */

import * as React from 'react';

import { RecountNotice } from '@/components/RecountNotice';
import { SortableTable, type SortableColumn } from '@/components/SortableTable';
import { TabErrorBoundary } from '@/components/TabErrorBoundary';
import { Card, CardContent } from '@/components/ui/card';
import { UserLabel } from '@/components/ui/user-label';
import { usePracticeDrills, usePracticeQuarter } from '@/hooks/use-practice';
import { useSyncStatus } from '@/hooks/use-sync-status';
import { useUrlFilters } from '@/hooks/use-url-filters';
import { formatDateTimeWithLabel } from '@/lib/format-date';
import { ageToColor, ageToHuman, COLOR_CLASS } from '@/lib/stale';
import type {
  PracticeDrillJudgement,
  PracticeDrillRating,
  PracticeDrillRow,
  PracticeQuarterRow,
} from '@/services/api-types';

/** A share derived from the two counts printed beside it, so the percentage and its
 *  inputs are the same arithmetic and cannot disagree on screen. Nothing to divide by is
 *  an em-dash, never `0%` — "we counted nothing" and "a real zero" are different facts. */
function share(numerator: number, denominator: number): string {
  if (!denominator) return '—';
  return `${((numerator / denominator) * 100).toFixed(0)}%`;
}

/** A spread as one cell: the middle value with the range under it. Both, always — a
 *  median alone hides a quarter of one long drill and eleven two-turn ones, and a mean
 *  score alone hides a 30 and a 90 behind the same 60. */
function spread(s: { median: number; min: number; max: number } | null): React.ReactNode {
  if (!s) return '—';
  return (
    <span className="tabular-nums">
      {s.median}
      <span className="text-muted-foreground text-xs"> ({s.min}–{s.max})</span>
    </span>
  );
}

/** The scene as a manager can act on it: the catalogue id, and how hard it is. Never the
 *  scene's own text. A scene the build cannot place says so rather than reading as easy. */
function sceneLabel(scene: string | null, difficulty: number | null): string {
  const name = scene ?? 'his own scenario';
  return difficulty === null ? `${name} (difficulty unknown)` : `${name} · difficulty ${difficulty}`;
}

// ============================================================================
// FRESHNESS — the sentence that stops a zero being read as a fact
// ============================================================================

/**
 * HOW FRESH THE MIRROR IS, said in the page's own words rather than left to the small grey
 * chip in the top-right chrome.
 *
 * It reuses the sync-status query every tab already polls (`useSyncStatus`), so this costs
 * no extra request — TanStack Query serves both subscribers from one in-flight fetch — and
 * one definition of "stale" (`ageToColor`) rather than a second threshold that could
 * disagree with the dot two inches away.
 *
 * FOUR STATES, and three of them are LOUD, because the quiet failure is the expensive one:
 * a reader who cannot see that the mirror stopped six hours ago reads an empty page as a
 * quiet week and writes that into somebody's review.
 */
function Freshness({
  ageMs,
  isLoading,
  isError,
}: {
  ageMs: number | null | undefined;
  isLoading: boolean;
  isError: boolean;
}): React.ReactElement {
  if (isLoading) {
    return (
      <p className="text-sm text-muted-foreground" data-testid="practice-freshness">
        Checking how fresh these counts are…
      </p>
    );
  }

  // UNKNOWN IS NOT FRESH. A failed or absent sync read means nobody can say how much of
  // the last week is below, so the page says exactly that instead of nothing.
  if (isError || ageMs === null || ageMs === undefined) {
    return (
      <p
        className="text-sm font-medium text-destructive"
        data-testid="practice-freshness"
        data-fresh="unknown"
      >
        Cannot tell how fresh this is — the sync status could not be read. Treat every count
        below as a floor and not as a fact: a drill that is missing here may simply not have
        been copied across yet.
      </p>
    );
  }

  const colour = ageToColor(ageMs);
  const stale = colour === 'red' || colour === 'dark-red';

  if (stale) {
    return (
      <p
        className="text-sm font-medium text-destructive"
        data-testid="practice-freshness"
        data-fresh="stale"
      >
        <span
          className={`inline-block h-2 w-2 rounded-full mr-2 align-middle ${COLOR_CLASS[colour]}`}
          aria-hidden
        />
        These counts are {ageToHuman(ageMs)} out of date — the copy of the trace store this
        page reads has stopped keeping up. Anything drilled since then is missing, so a low
        number below does NOT mean nobody practised.
      </p>
    );
  }

  return (
    <p
      className="text-sm text-muted-foreground"
      data-testid="practice-freshness"
      data-fresh="fresh"
    >
      <span
        className={`inline-block h-2 w-2 rounded-full mr-2 align-middle ${COLOR_CLASS[colour]}`}
        aria-hidden
      />
      Copied from the trace store {ageToHuman(ageMs)}. A drill run since then is not here yet.
    </p>
  );
}

// ============================================================================
// BLOCK 1 — the people
// ============================================================================

/**
 * ONE ROW PER PERSON WHO PRACTISED IN THE PERIOD. Headers are short because a header is a
 * label; what a column means is said once in the sentence above the page, not eleven times
 * in the header cells.
 *
 * THE COVERAGE TRAVELS WITH THE SCORE, on the same row and not in a block underneath it:
 * "Scenes / runs" and "Difficulty" are what tell twenty runs of one easy scene from ten
 * different ones at four difficulties, and a score read without them is the one number
 * §19.4's surviving half exists to prevent.
 */
const QUARTER_COLUMNS: Array<SortableColumn<PracticeQuarterRow>> = [
  {
    key: 'realName',
    header: 'Name',
    // `stacked` keeps the handle under the real name — the page no longer spends a whole
    // column on it, and two new hires called the same thing must still be told apart.
    format: (_v, r) => <UserLabel name={r.realName} username={r.userId} variant="stacked" />,
  },
  {
    key: 'sessions',
    header: 'Sessions',
    align: 'right',
    // "N started, M finished" — and the page stops there. The difference is NOT printed as
    // a count of abandoned drills, because one of them may be running right now.
    format: (_v, r) => (
      <span className="tabular-nums">
        {r.sessions}
        <span className="text-muted-foreground text-xs"> ({r.closed} finished)</span>
      </span>
    ),
  },
  {
    key: 'score',
    // MEAN, said in the header, because `spread` draws this cell and the turn cell with
    // one piece of code and nothing else on screen tells a mean from a median.
    header: 'Mean score',
    align: 'right',
    format: (_v, r) => (
      <div data-testid={`practice-score-${r.userId ?? 'unknown'}`}>
        {/* The same helper the turn spread uses, so a mean and a median are rendered by
            one piece of code and cannot drift apart on screen. */}
        {spread(r.score && { median: r.score.mean, min: r.score.min, max: r.score.max })}
        {/* THE ONE LIE THIS TABLE COULD TELL: a mean over two of twenty drills reading
            like a mean over twenty. Said on the row, next to the number it qualifies. */}
        {r.scored < r.sessions && (
          <div className="text-muted-foreground text-xs" data-testid="practice-score-caveat">
            Scores over {r.scored} of {r.sessions} drills
          </div>
        )}
      </div>
    ),
  },
  {
    // THE SECOND NUMBER, AND IT ANSWERS A DIFFERENT QUESTION FROM THE ONE BESIDE IT. `Mean
    // score` is counted from the record; this is a model's reading of how he played. They
    // are next to each other because a manager wants both, and they are never averaged
    // together because they are not measurements of the same thing.
    key: 'rating',
    // MEAN, said in the header, for the reason `Mean score` beside it says it: `spread`
    // draws this cell and the turns cell with one piece of code, and nothing else on
    // screen tells a mean from a median.
    header: "Mean judge's rating",
    align: 'right',
    format: (_v, r) => (
      <div data-testid={`practice-rating-${r.userId ?? 'unknown'}`}>
        {/* The same helper the score and the turns use — and `null` draws an em-dash, never
            a zero: "no drill of his was rated" is not "the judge rated him at nothing". */}
        {spread(r.rating && { median: r.rating.mean, min: r.rating.min, max: r.rating.max })}
        {/* THE SAME LIE THE SCORE COLUMN CAN TELL, one column over: a mean over two of
            twenty drills reading like a mean over twenty. And the two counts differ — the
            rating is its own model call, so a drill can be scored and unrated.

            OVER THE DRILLS THAT FINISHED, exactly as `Judged` two columns over, and for the
            same reason: the rating call starts on the turn that CLOSES a drill, so
            `rated <= closed` structurally. Divided by everything he STARTED, a row of
            twenty drills of which four closed and all four were rated printed "Rated over 4
            of 20" beside "4 (100% of finished)" — two cells of one row saying opposite
            things about the same four drills. */}
        {r.rated < r.closed && (
          <div className="text-muted-foreground text-xs" data-testid="practice-rating-caveat">
            Rated over {r.rated} of {r.closed} finished
          </div>
        )}
        {/* AND WHAT THE NUMBER IS NOT, ON THE ROW ITSELF. The expanded panel says it in
            full, but the table is the surface a manager copies into a review without ever
            opening a session — a number that can be quoted has to carry its limit where it
            is read. Printed off the payload's own bit, so the day the question is
            calibrated on material nobody wrote for it the line disappears by itself. */}
        {r.ratingUncalibrated && (
          <div
            className="text-destructive text-xs font-medium"
            data-testid="practice-rating-uncalibrated-row"
          >
            Not calibrated — compares his own drills, not how he sells
          </div>
        )}
      </div>
    ),
  },
  {
    key: 'judged',
    header: 'Judged',
    align: 'right',
    // OVER THE DRILLS THAT FINISHED, never over the drills he started. The judge only runs
    // on the turn that CLOSES a drill, so `judged <= closed` structurally: ten drills of
    // which four closed and all four were judged is a judge that missed nothing, and
    // dividing by ten printed it as 40% — a broken system, on a healthy one. The
    // denominator differs from every other share on this row, so the cell names it.
    format: (_v, r) => (
      <span className="tabular-nums">
        {r.judged}
        <span className="text-muted-foreground text-xs">
          {' '}
          ({share(r.judged, r.closed)} of finished)
        </span>
      </span>
    ),
  },
  {
    // DISTINCT SCENES OVER TOTAL RUNS — the coverage half of the rule above.
    key: 'scenes',
    header: 'Scenes / runs',
    align: 'right',
    format: (_v, r) => (
      <span className="tabular-nums">
        {r.scenes.length} / {r.sessions}
      </span>
    ),
  },
  {
    // A RANGE AND NEVER A MEAN. "2–5" is readable; "average 3.3" hides one trivial scene
    // and one hard one behind the same number as a quarter of middling ones.
    key: 'difficulty',
    header: 'Difficulty',
    align: 'right',
    format: (_v, r) => (
      <>
        {r.difficulty ? (
          <span className="tabular-nums">
            {r.difficulty.min}–{r.difficulty.max}
          </span>
        ) : (
          // Not "he practised nothing hard": this build can place none of his scenes.
          <span data-testid="practice-difficulty-unplaceable">—</span>
        )}
        {/* WHENEVER THERE ARE ANY, not only when EVERY scene is unplaceable. Six catalogue
            scenes and four of his own is the ordinary case: the cell used to print "2–5"
            and say nothing, while those four drills still moved his mean. And the count is
            not "he described it himself" — a scene this build has RETIRED lands in it too,
            so it says the one thing true of both. */}
        {r.describedHisOwn > 0 && (
          <span
            className="text-muted-foreground text-xs"
            data-testid="practice-unplaceable-count"
          >
            {' '}
            ({r.describedHisOwn} the catalogue cannot place)
          </span>
        )}
      </>
    ),
  },
  {
    key: 'turnsSurvived',
    header: 'Median turns',
    align: 'right',
    format: (_v, r) => spread(r.turnsSurvived),
  },
  {
    // WHAT HE ASKED TO BE SHOWN — never merged with what he cracked in conversation. One
    // is help taken, the other is help earned, and a reader shown them merged learns
    // neither.
    key: 'cardsShown',
    header: 'Read the card',
    align: 'right',
    format: (_v, r) => (
      <span className="tabular-nums">
        {r.cardsShown}
        <span className="text-muted-foreground text-xs"> ({share(r.cardsShown, r.sessions)})</span>
      </span>
    ),
  },
  {
    key: 'unlockedTotal',
    header: 'Cracked unaided',
    align: 'right',
    format: (_v, r) => (
      <span className="tabular-nums">
        {r.unlockedTotal}
        <span className="text-muted-foreground text-xs">
          {' '}
          ({share(r.unlockedTotal, r.sessions)})
        </span>
      </span>
    ),
  },
  {
    key: 'lastPractisedAt',
    header: 'Last practised',
    format: (_v, r) => <span className="tabular-nums">{formatDateTimeWithLabel(r.lastPractisedAt)}</span>,
  },
];

// ============================================================================
// BLOCK 2 — the sessions
// ============================================================================

/** How the outcome is said in words a reader does not have to decode. */
const OUTCOME_WORDS: Record<string, string> = {
  stopped: 'he ended it',
  strikes: 'client walked out',
  ceiling: 'ran out of turns',
  running: 'still running',
  unfinished: 'never finished',
};

/**
 * THE WEAKNESS HALF IS A CLOSED TOKEN, NOT PROSE — `oneStandsOut` | `nothingStoodOut` |
 * `tooShortToShow`, written by the judge (`src/services/practice-judgement.ts`). It was
 * printed as itself until 2026-09-02, so every judged drill read "The weakness to work on:
 * nothingStoodOut" — a machine identifier under a label that says the OPPOSITE of what the
 * token means: `nothingStoodOut` is the finding that he did nothing notably wrong.
 *
 * Only the two no-weakness states are here. `oneStandsOut` has no fixed sentence because
 * the judge's own prose IS the weakness, and a token from a later build reaches the reader
 * as itself rather than being swallowed — the same rule `OUTCOME_WORDS` follows above.
 */
const WEAKNESS_WORDS: Record<string, string> = {
  nothingStoodOut: 'Nothing stood out as a weakness in this drill.',
  tooShortToShow: 'The drill was too short to show a weakness.',
};

/**
 * WHAT THE JUDGE SAID, AND THE WORDS IT SAID IT ABOUT.
 *
 * Each half is a claim followed by the fragment of the RM's OWN line it points at. The
 * quote is what makes the claim checkable by someone who was not there, so it is never
 * dropped WHERE THERE IS ONE — a verdict about a named person with nothing to check it
 * against is an opinion printed as a finding. The one state with no quote is the one with
 * no accusation either: no weakness stood out, so the judge points at no moment, and an
 * empty pair of quotation marks would invent a missing one.
 */
function Judgement({ j }: { j: PracticeDrillJudgement }): React.ReactElement {
  return (
    <div className="space-y-2 text-sm" data-testid="practice-judgement">
      <p>
        <span className="font-medium">Did well:</span> {j.didWell}
      </p>
      <blockquote
        className="border-l-2 border-border pl-3 text-muted-foreground italic"
        data-testid="practice-judgement-well-quote"
      >
        “{j.didWellPointsAt}”
      </blockquote>
      {j.theWeakness === 'oneStandsOut' ? (
        <p data-testid="practice-judgement-weakness">
          <span className="font-medium">The weakness to work on:</span> {j.didBadly}
        </p>
      ) : (
        // NO HEADING HERE. There is no weakness to work on, and a heading announcing one
        // over a sentence saying there is none is the defect this branch exists to stop.
        // AND THE JUDGE'S OWN SENTENCE AFTER IT. In these two states the writer's contract
        // makes `didBadly` say WHICH of them holds and WHY, so dropping it here throws away
        // the only part a manager could not have worked out from the token. It usually
        // restates the fixed sentence and sometimes does not, and the times it does not are
        // the whole reason to print it.
        <p data-testid="practice-judgement-weakness">
          {WEAKNESS_WORDS[j.theWeakness] ?? j.theWeakness} {j.didBadly}
        </p>
      )}
      {/* EMPTY BY CONTRACT whenever the weakness is not `oneStandsOut` — the writer
          supports and tests that state — and an empty pair of quotation marks under a
          verdict about a named person reads as a quote nobody can find in the drill. */}
      {j.didBadlyPointsAt && (
        <blockquote
          className="border-l-2 border-border pl-3 text-muted-foreground italic"
          data-testid="practice-judgement-badly-quote"
        >
          “{j.didBadlyPointsAt}”
        </blockquote>
      )}
      {/* A verdict about a named man must not read as measured when nothing has measured
          it. The caveat is loud enough to be read and short enough to be read every time. */}
      {j.notCalibrated && (
        <p className="text-xs font-medium text-destructive" data-testid="practice-uncalibrated">
          Not calibrated: the question this judge was asked has never been checked against a
          drill known to be good and one known to be bad, so this is a reading and not a
          measurement.
        </p>
      )}
      <p className="text-xs text-muted-foreground">Judge question: {j.question}</p>
    </div>
  );
}

/**
 * THE NUMBER, THE SENTENCE THAT DECIDED IT, AND WHAT THE NUMBER IS NOT.
 *
 * The caveat is printed OFF THE FIELD (`calibratedOnWrittenDrillsOnly`) and never
 * hard-coded, so the day a build calibrates this question against material nobody wrote
 * for it, the field arrives false and the caveat stops appearing on its own — nothing here
 * has to be remembered and edited.
 *
 * IT IS SAID IN PLAIN WORDS AND NOT IN THE WORD "CALIBRATION". What a manager needs to
 * know is that the number compares this salesperson's own drills with each other and is
 * not evidence about how he sells to real clients, and a reader who cannot restate that
 * has been told nothing.
 */
function Rating({ r }: { r: PracticeDrillRating }): React.ReactElement {
  return (
    <div className="space-y-2 text-sm" data-testid="practice-rating">
      <p>
        <span className="font-medium">Judge&rsquo;s rating: {r.score}/100.</span> {r.why}
      </p>
      {r.calibratedOnWrittenDrillsOnly && (
        <p
          className="text-xs font-medium text-destructive"
          data-testid="practice-rating-uncalibrated"
        >
          This rating was only ever checked against two drills WRITTEN for the rubric it
          uses, so it compares this salesperson&rsquo;s drills with each other. It is not
          evidence about how he sells to real clients.
        </p>
      )}
      <p className="text-xs text-muted-foreground">Rating question: {r.question}</p>
    </div>
  );
}

/**
 * ONE SESSION, with the judgement and the debrief behind a click on the row.
 *
 * THE DEBRIEF IS ON THIS PAGE and the full transcript is not, and that is a decision rather
 * than an omission. The debrief is the coach's review OF a conversation that has finished.
 * The transcript is the drill itself, and every in-character turn in it is the CLIENT
 * speaking — reading it is reading the persona, the hidden objection and, at the end, the
 * card. Whoever needs one goes to the trace store for that single drill.
 */
function DrillRow({ drill }: { drill: PracticeDrillRow }): React.ReactElement {
  const [open, setOpen] = React.useState(false);

  return (
    <Card data-testid="practice-drill">
      <CardContent className="p-3 space-y-2">
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          aria-expanded={open}
          className="w-full text-left"
          data-testid="practice-drill-toggle"
        >
          <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1 text-sm">
            <span className="tabular-nums text-muted-foreground">{formatDateTimeWithLabel(drill.startedAt)}</span>
            <UserLabel name={drill.realName} username={drill.userId} variant="inline" />
            <span>{sceneLabel(drill.scene, drill.difficulty)}</span>
            <span className="tabular-nums">
              {/* Turns against the scene's own ceiling — the pair, never the bare count. */}
              {drill.turns ?? '—'}
              {drill.ceiling !== null && `/${drill.ceiling}`} turns
            </span>
            <span className="tabular-nums">{drill.strikes ?? '—'} strikes</span>
            <span className="font-medium" data-testid="practice-drill-outcome">
              {OUTCOME_WORDS[drill.outcome] ?? drill.outcome}
            </span>
            {/* ASKED FOR, then EARNED — two facts, two words, never one column. */}
            <span>{drill.unlocked ? 'cracked him' : 'never cracked him'}</span>
            <span className="text-muted-foreground">
              {drill.cardsShown ? 'read the card' : 'no hint taken'}
            </span>
            <span className="tabular-nums font-medium" data-testid="practice-drill-score">
              {/* A drill in flight scores nothing: a score for a conversation still being
                  played is a lie, and a zero would read as a bad drill. */}
              {drill.score === null ? '— score' : `score ${drill.score}/100`}
            </span>
            {/* THE JUDGE'S NUMBER, LABELLED, because two figures out of 100 side by side
                with nothing to tell them apart is worse than one. A drill the judge never
                rated SAYS SO rather than leaving a gap a reader has to interpret — the
                rating is its own model call and it can fail on a drill that scored fine. */}
            <span className="tabular-nums font-medium" data-testid="practice-drill-rating">
              {drill.rating === null ? '— judge rating' : `judge ${drill.rating.score}/100`}
              {/* AND ITS LIMIT WHERE THE NUMBER IS, not only behind the click. This list is
                  the owner's second cut and is scanned the way the table above it is: a
                  manager quotes `judge 62/100` into a review without expanding anything,
                  which is the argument the table's own caveat line was accepted on. Two
                  words and a title rather than the full sentence — the panel below carries
                  that in full, and repeating it on thirty rows would make it unreadable. */}
              {drill.rating?.calibratedOnWrittenDrillsOnly && (
                <span
                  className="text-destructive text-xs font-normal"
                  data-testid="practice-drill-rating-uncalibrated"
                  title={'Checked only against two drills WRITTEN for the rubric it uses, '
                    + "so it compares this salesperson's drills with each other. It is not "
                    + 'evidence about how he sells to real clients.'}
                >
                  {' '}not calibrated
                </span>
              )}
            </span>
          </div>
        </button>

        {/* The fact the two derived outcomes were read from. Shown on exactly those two,
            because on a drill that recorded how it ended the clock decided nothing. */}
        {(drill.outcome === 'running' || drill.outcome === 'unfinished') && (
          <p className="text-xs text-muted-foreground" data-testid="practice-drill-basis">
            No ending was recorded for this drill; its last turn was {formatDateTimeWithLabel(drill.lastTurnAt)}.
          </p>
        )}

        {open && (
          <div className="space-y-3 pt-1" data-testid="practice-drill-detail">
            {drill.rating && <Rating r={drill.rating} />}

            {drill.judgement ? (
              <Judgement j={drill.judgement} />
            ) : (
              // IT NAMES THE WRITTEN ASSESSMENT, NEVER "THE JUDGE". Both model calls are
              // called the judge on this page, and the rating is printed four lines above
              // this one: "Judge's rating: 88/100" over "The judge never assessed this
              // drill" is a panel contradicting itself about the same drill. What is
              // missing here is the PROSE — what he did well and badly, with his own words
              // quoted — and saying that leaves both statements true.
              <p className="text-xs text-muted-foreground" data-testid="practice-no-judgement">
                No written assessment of this drill — nothing was recorded about what he did
                well or badly, so there is nothing to read here.
              </p>
            )}

            {drill.debrief.length > 0 ? (
              <div className="space-y-2" data-testid="practice-debrief">
                {drill.debrief.map((turn, i) => (
                  <p key={i} className="text-sm whitespace-pre-wrap border-l-2 border-border pl-3">
                    {turn}
                  </p>
                ))}
              </div>
            ) : (
              <p className="text-xs text-muted-foreground">
                No debrief — this drill never reached the review.
              </p>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// ============================================================================

export function PracticePage(): React.ReactElement {
  const { filters, setPending } = useUrlFilters();
  const quarter = usePracticeQuarter(filters);
  const drills = usePracticeDrills(filters);
  const sync = useSyncStatus();

  const quarterRows = quarter.data ?? [];
  const drillRows = drills.data ?? [];
  const unkeyedTurns = quarterRows.reduce((n, r) => n + r.unkeyedTurns, 0);

  /* CLICKING A PERSON SCOPES THE SESSION LIST TO HIM — through the global user filter both
     queries already send to the backend (`filters.users`, honoured by
     `_practiceQuarterWhere`). No second selection state, so the list below and the chip in
     the filter bar cannot disagree about who is being read. The anonymous row names nobody
     to scope to, hence the guard. */
  const scopeToUser = React.useCallback(
    (row: PracticeQuarterRow) => {
      if (row.userId) setPending({ users: [row.userId] });
    },
    [setPending],
  );

  if (quarter.isError || drills.isError) {
    return (
      <Card>
        <CardContent className="p-4 space-y-2">
          <div className="text-sm font-medium text-red-600">Practice failed to load</div>
          <button
            type="button"
            onClick={() => {
              void quarter.refetch();
              void drills.refetch();
            }}
            className="text-xs underline text-primary hover:no-underline"
          >
            Retry
          </button>
        </CardContent>
      </Card>
    );
  }

  return (
    <TabErrorBoundary name="Practice">
      <section aria-labelledby="practice-heading" className="space-y-4">
        <header className="space-y-1">
          <h1 id="practice-heading" className="text-2xl font-semibold">
            Practice
          </h1>
          {/* ONE SENTENCE ON WHAT THE SCORE IS MADE OF. A number nobody can explain back is
              a number nobody trusts, and the parts are all on the row beside it. */}
          <p className="text-sm text-muted-foreground">
            Each session scores out of 100 from four things: whether he reached the debrief,
            how far he got against that scene&rsquo;s own turn limit, whether the client&rsquo;s
            hidden objection came out and whether he got there unaided or read the card, and
            whether the client stayed after a mistake or walked out &mdash; then adjusted
            for how hard the scene is. Beside it is a second number out of 100, the
            judge&rsquo;s rating: that one is not counted from the record at all but given by
            a model reading the conversation, so the two answer different questions and a
            gap between them is worth looking at rather than an error.
            Practice is not counted in the operational numbers on the other tabs.
          </p>
          <Freshness
            ageMs={sync.data?.ageMs}
            isLoading={sync.isLoading}
            isError={sync.isError}
          />
          <RecountNotice queries={[quarter, drills]} />
        </header>

        <div className="space-y-2">
          <h2 className="text-lg font-medium">Who practised, over the selected period</h2>
          <SortableTable
            tableId="practice-quarter"
            rows={quarterRows}
            columns={QUARTER_COLUMNS}
            getRowKey={(r) => r.userId ?? `(unknown-${quarterRows.indexOf(r)})`}
            onRowClick={scopeToUser}
            isLoading={quarter.isLoading}
            emptyMessage="No drills in the selected period"
          />
          {/* A turn carrying no session key belongs to no session anyone can count, so it
              is reported rather than dropped — the same reason the freshness line exists.
              Without it every number in the table above is silently short and nothing on
              screen says by how much. One line for the page, not a card per person. */}
          {unkeyedTurns > 0 && (
            <p className="text-xs text-muted-foreground" data-testid="practice-unkeyed">
              {unkeyedTurns} drill turns in this period carry no session marker — they were
              recorded before drills were grouped into sessions, so they belong to no session
              in the list below and every count above is short by that many turns.
            </p>
          )}
        </div>

        <div className="space-y-2">
          <h2 className="text-lg font-medium">The sessions, newest first</h2>
          {drills.isLoading ? (
            <div
              data-testid="practice-drills-skeleton"
              className="h-24 rounded bg-muted animate-pulse"
            />
          ) : drillRows.length === 0 ? (
            <Card>
              <CardContent className="p-4 text-sm text-muted-foreground">
                No drills in the selected period.
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-2">
              {drillRows.map((d) => (
                <DrillRow key={`${d.userId ?? ''}|${d.startedAt}`} drill={d} />
              ))}
            </div>
          )}
        </div>
      </section>
    </TabErrorBoundary>
  );
}
